import {createContext, useContext} from 'react';
import {immer} from 'zustand/middleware/immer';
import {devtools} from 'zustand/middleware';
import {shallow} from 'zustand/shallow';
import {useStoreWithEqualityFn} from 'zustand/traditional';
import type {StateCreator} from 'zustand/vanilla';
import {createStore as zustandCreateStore} from 'zustand';

export type StoreOptions = {
  name: string;
  version: number;
  devtools?: boolean;
};

type State = unknown;
export type StoreApi<S extends State> = ReturnType<typeof createStore<S>>;
export type StoreCreator<S extends State> = Parameters<typeof createStore<S>>[0];

export const createStore = <S extends State>(
  storeCreator: StateCreator<S, [['zustand/immer', never]]>,
  options: StoreOptions,
) => {
  const name = `linx@${options.name}@${options.version}`;

  return zustandCreateStore<S>()(
    devtools(immer(storeCreator), {name, enabled: process.env.NODE_ENV !== `production`}),
  );
};

export const createStoreContext = <T, TContext extends StoreApi<T> = ReturnType<typeof createStore<T>>>() => {
  const StoreContext = createContext<TContext | null>(null);

  const useStore = <U>(selector: (state: T) => U, equalityFn: (left: U, right: U) => boolean = shallow): U => {
    const store = useContext(StoreContext);
    if (!store) throw new Error(`Missing StoreContext.Provider in the tree`);
    return useStoreWithEqualityFn(store, selector, equalityFn);
  };

  const useStoreApi = () => {
    const store = useContext(StoreContext);
    if (!store) throw new Error(`Missing StoreContext.Provider in the tree`);
    return store;
  };

  return {StoreContext, useStore, useStoreApi};
};
